import React from "react";
import { AnimatePresence } from "framer-motion";
import { FaParking } from "react-icons/fa";
// import { FaEllipsis } from "react-icons/fa6";
import { HiOutlineMap, HiViewGridAdd } from "react-icons/hi";
import { IconType } from "react-icons/lib";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import PoiList from "./PoiList";
import Filters from "./Filters";
import RouteForm from "./RouteForm";
import PoiDetails from "./PoiDetails";
import InfoBox from "./InfoBox";

import useMapStore from "@/store/mapStore";
import MoreAccordion from "./MoreAccordion";
import { FilterItemModel } from "@/models/filterItem.model";
import CityRouteForm from "./CityRouteForm";

function Sidebar() {
  const activeFilter = useMapStore((state) => state.activeFilter);
  const routeFinish = useMapStore((state) => state.routeFinish);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const gpsMode = useMapStore((state) => state.gpsMode);
  const showCustomRoutePicker = useMapStore(
    (state) => state.showCustomRoutePicker,
  );
  const sidebarMinified = useMapStore((state) => state.sidebarMinified);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const activeMenuItem = useMapStore((state) => state.activeMenuItem);
  const startFeature = useMapStore((state) => state.startFeature);
  const navMode = useMapStore((state) => state.navMode);
  const cityStart = useMapStore((state) => state.cityStart);
  const cityFinish = useMapStore((state) => state.cityFinish);
  const stops = useMapStore((state) => state.stops);
  const appIsIdle = useMapStore((state) => state.appIsIdle);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const setActiveMenuItem = useMapStore((state) => state.setActiveMenuItem);
  const setOverlayContent = useMapStore((state) => state.setOverlayContent);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const setGlobalSearchOpen = useMapStore((state) => state.setGlobalSearchOpen);

  const findMyCarEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_FIND_MY_CAR === "true";

  const clickHandler = (id: string) => {
    if (id === "route") {
      setGlobalSearchOpen(true);
      return;
    }
    setActiveMenuItem(id);
    if (id === "findMyCar") {
      setOverlayContent("findMyCar");
      return;
    } else {
      setOverlayContent(undefined);
    }

    if (id !== "categories") {
      setActiveFilter({} as FilterItemModel);
    }

    if (id === "menu" && activeMenuItem === "menu") {
      setActiveMenuItem("map");
    }
  };

  const MenuItem = ({
    id,
    title,
    icon,
    className,
    onClick,
  }: {
    id: string;
    title?: string;
    icon?: IconType;
    className?: string;
    onClick: (id: string) => void;
  }) => {
    return (
      <li
        className={cn(
          "flex cursor-pointer items-center gap-1 rounded-full px-2 py-1.5 xs:px-4",
          activeMenuItem === id
            ? "bg-primary text-white"
            : "hover:bg-primary-foreground",
          className,
        )}
        onClick={() => onClick(id)}
      >
        {icon && (
          <span className={cn("text-base xs:text-xl")}>
            {React.createElement(icon)}
          </span>
        )}
        {title && <span className="text-sm">{title}</span>}
      </li>
    );
  };

  if (mobileMode) {
    return (
      <>
        <div
          className={cn(
            "absolute left-2 right-2 top-[calc(100dvh-70px)] z-[101] rounded-full bg-white p-2 lg:top-[calc(100%-70px)]",
            activeMenuItem === "map" &&
              "shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",
            showCustomRoutePicker && "hidden",
          )}
        >
          <ul
            className={cn(
              "flex items-center justify-between gap-2 capitalize xs:gap-2",
              !findMyCarEnabled && "px-10",
            )}
          >
            <MenuItem
              id="categories"
              title={t("categories")}
              icon={HiViewGridAdd}
              onClick={clickHandler}
            />
            <MenuItem
              id="route"
              title={t("route")}
              icon={HiOutlineMap}
              onClick={clickHandler}
            />
            {findMyCarEnabled && (
              <MenuItem
                id="findMyCar"
                title={t("find-my-car")}
                icon={FaParking}
                onClick={clickHandler}
              />
            )}
            {/*<MenuItem
              id="menu"
              title={t("more")}
              icon={FaEllipsis}
              onClick={clickHandler}
            />*/}
          </ul>
        </div>
        <AnimatePresence>
          {activeMenuItem === "categories" &&
            Object.keys(routeFinish).length === 0 &&
            Object.keys(activeFilter).length === 0 && <Filters key="1" />}
          {activeMenuItem === "menu" && <MoreAccordion />}
          {activeFilter?.type === "list" && !routeFinish?.id && (
            <PoiList key="2" />
          )}
          {((activeFilter?.type === "closest" && !kioskMode && !gpsMode) ||
            (showCustomRoutePicker &&
              !startFeature?.id &&
              navMode === "mall")) && <RouteForm key="3" />}
          {showCustomRoutePicker &&
            navMode !== "mall" &&
            (!cityStart?.title || !cityFinish?.title) && (
              <CityRouteForm key="3" />
            )}
          {activeFilter?.type === "info" && <InfoBox key="4" />}
          {((routeFinish?.id && !showCustomRoutePicker) ||
            (showCustomRoutePicker && startFeature?.id && routeFinish?.id) ||
            (navMode !== "mall" && cityStart?.title && cityFinish?.title)) && (
            <PoiDetails key="5" />
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <div
      className={cn(
        "",
        sidebarMinified ? "w-24" : "w-[380px] pt-28",
        (routeFinish?.id && !showCustomRoutePicker) ||
          (stops.length > 1 && !showCustomRoutePicker) ||
          (navMode !== "mall" && cityStart?.title && cityFinish?.title)
          ? "h-screen"
          : "relative z-10 h-screen rounded-r-2xl bg-white shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] kiosk:flex kiosk:w-0 kiosk:shadow-none selection:kiosk:bg-transparent",
        ((activeFilter?.type === "closest" && !kioskMode && !gpsMode) ||
          showCustomRoutePicker) &&
          "h-screen w-[520px] bg-transparent shadow-none kiosk:w-[520px]",
        appIsIdle && "kiosk:w-0",
      )}
    >
      <AnimatePresence>
        {Object.keys(routeFinish).length === 0 &&
          (Object.keys(activeFilter).length === 0 || verticalKioskMode) &&
          navMode === "mall" && <Filters key="1" />}
        {activeFilter?.type === "list" && !showCustomRoutePicker && (
          <>
            {verticalKioskMode && routeFinish?.id ? <></> : <PoiList key="2" />}
          </>
        )}
        {((activeFilter?.type === "closest" && !kioskMode && !gpsMode) ||
          (showCustomRoutePicker &&
            !startFeature?.id &&
            navMode === "mall")) && <RouteForm key="3" />}
        {showCustomRoutePicker &&
          navMode !== "mall" &&
          (!cityStart?.title || !cityFinish?.title) && (
            <CityRouteForm key="3" />
          )}
        {activeFilter?.type === "info" && <InfoBox key="4" />}
        {((routeFinish?.id && !showCustomRoutePicker) ||
          (stops.length > 1 && !showCustomRoutePicker) ||
          (showCustomRoutePicker && startFeature?.id && routeFinish?.id) ||
          (navMode !== "mall" && cityStart?.title && cityFinish?.title)) && (
          <PoiDetails key="5" />
        )}
      </AnimatePresence>
    </div>
  );
}

export default Sidebar;
